<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  data: {
    type: Array,
    required: true,
    default: () => []
  },
  height: {
    type: String,
    default: '250px'
  },
  showBrushInstructions: {
    type: Boolean,
    default: true
  },
  highlightedLogType: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['bar-click', 'brush-select'])

// Consistent color palette for log types
const LOG_TYPE_COLORS = {
  conn: '#7357D2',      // Purple (primary)
  dns: '#4A90D9',       // Blue
  http: '#50C878',      // Green
  ssl: '#F5A623',       // Orange
  x509: '#D94A4A',      // Red
  files: '#9B59B6',     // Violet
  smtp: '#1ABC9C',      // Teal
  ssh: '#E74C3C',       // Coral
  dhcp: '#3498DB',      // Light blue
  ntp: '#95A5A6',       // Gray
  rdp: '#E67E22',       // Dark orange
  ftp: '#27AE60',       // Dark green
  sip: '#8E44AD',       // Dark purple
  snmp: '#2980B9',      // Dark blue
  kerberos: '#C0392B',  // Dark red
  smb: '#16A085',       // Dark teal
  dce_rpc: '#D35400',   // Rust
  ntlm: '#7F8C8D',      // Dark gray
  pe: '#BDC3C7',        // Light gray
  weird: '#E91E63',     // Pink
  notice: '#FF5722',    // Deep orange
  software: '#607D8B',  // Blue gray
  known: '#795548',     // Brown
  _default: '#6B7280'   // Fallback gray
}

// Get color for a log type
function getLogTypeColor(logType) {
  return LOG_TYPE_COLORS[logType] || LOG_TYPE_COLORS._default
}

// Chart
const chartContainer = ref(null)
let chartInstance = null

// Collect all unique log types from data
const allLogTypes = computed(() => {
  const types = new Set()
  if (props.data) {
    props.data.forEach(bucket => {
      if (bucket.counts) {
        Object.keys(bucket.counts).forEach(lt => types.add(lt))
      }
    })
  }
  // Sort alphabetically for consistent ordering
  return Array.from(types).sort()
})

// Calculate max total count for y-axis
const maxTotalCount = computed(() => {
  if (!props.data || props.data.length === 0) return 0
  return Math.max(...props.data.map(bucket => {
    if (bucket.counts) {
      return Object.values(bucket.counts).reduce((sum, c) => sum + c, 0)
    }
    return bucket.count || 0
  }))
})

// Watch for data changes and update chart
watch([() => props.data, chartContainer], ([newData]) => {
  if (chartContainer.value && newData && newData.length > 0) {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      updateHistogram()
    }, 100)
  }
}, { immediate: false, deep: true })

// Watch for highlightedLogType changes to update opacity
watch(() => props.highlightedLogType, () => {
  if (chartInstance && props.data && props.data.length > 0) {
    updateHistogram()
  }
})

// Helper: Calculate interval between histogram buckets
function calculateInterval() {
  let interval = 60000 // Default 1 minute in ms
  if (props.data && props.data.length > 1) {
    interval = props.data[1].timestamp - props.data[0].timestamp
  }
  return interval
}

// Helper: Find bar boundaries and round timestamp appropriately
function findBarBoundaries(timestamp, roundDirection = 'backward') {
  if (!props.data || props.data.length === 0) return null

  const interval = calculateInterval()

  // For backward rounding (start of brush): find the bar that starts at or before timestamp
  // For forward rounding (end of brush): find the bar that contains or comes after timestamp
  if (roundDirection === 'backward') {
    // Find the rightmost bar whose start is <= timestamp
    for (let i = props.data.length - 1; i >= 0; i--) {
      const barStart = props.data[i].timestamp
      if (barStart <= timestamp) {
        return barStart
      }
    }
    // If timestamp is before all bars, return first bar start
    return props.data[0].timestamp
  } else {
    // Forward rounding: find the leftmost bar whose end is >= timestamp
    for (let i = 0; i < props.data.length; i++) {
      const barStart = props.data[i].timestamp
      const barEnd = barStart + interval
      if (barEnd >= timestamp) {
        return barEnd
      }
    }
    // If timestamp is after all bars, return last bar end
    const lastBarStart = props.data[props.data.length - 1].timestamp
    return lastBarStart + interval
  }
}

// Build stacked series for each log type
function buildStackedSeries() {
  const logTypes = allLogTypes.value

  // For each timestamp, find which log type is on top (last non-zero in stack order)
  const topLogTypePerBucket = props.data.map(bucket => {
    let topType = null
    for (const lt of logTypes) {
      if (bucket.counts?.[lt] > 0) {
        topType = lt
      }
    }
    return topType
  })

  return logTypes.map((logType) => {
    const isHighlighted = !props.highlightedLogType || props.highlightedLogType === logType
    const color = getLogTypeColor(logType)

    return {
      name: logType,
      type: 'bar',
      stack: 'total',
      data: props.data.map((bucket, idx) => {
        const value = bucket.counts?.[logType] || 0
        const isTopSegment = topLogTypePerBucket[idx] === logType
        return {
          value: [bucket.timestamp, value],
          itemStyle: {
            borderRadius: isTopSegment ? [3, 3, 0, 0] : [0, 0, 0, 0]
          }
        }
      }),
      itemStyle: {
        color: color,
        opacity: isHighlighted ? 0.95 : 0.45
      },
      emphasis: {
        itemStyle: {
          opacity: 1
        }
      },
      barWidth: '90%'
    }
  })
}

// Update histogram
function updateHistogram() {
  if (!chartContainer.value) {
    return
  }

  // Initialize chart if needed
  if (!chartInstance) {
    chartInstance = echarts.init(chartContainer.value)
  }

  // Check if we have histogram data
  if (!props.data || props.data.length === 0) {
    chartInstance.setOption({
      title: {
        text: 'No timeline data available',
        left: 'center',
        top: 'center',
        textStyle: { color: '#6D7785', fontSize: 14 }
      },
      grid: { left: 0, right: 0, top: 0, bottom: 0 }
    })
    return
  }

  // Check if data has stacked format (counts object) or legacy format (count number)
  const isStackedFormat = props.data[0]?.counts !== undefined

  // Tooltip formatter
  const tooltipFormatter = (params) => {
    if (!params || params.length === 0) return ''
    const date = new Date(params[0].value[0])
    let tooltip = `<div style="font-weight:600;margin-bottom:6px">${date.toLocaleString()}</div>`

    if (isStackedFormat) {
      // Stacked format: show breakdown by log type
      let total = 0
      // Sort by count descending for tooltip
      const sortedParams = [...params].sort((a, b) => b.value[1] - a.value[1])

      sortedParams.forEach(p => {
        if (p.value[1] > 0) {
          const isHighlighted = !props.highlightedLogType || props.highlightedLogType === p.seriesName
          const opacity = isHighlighted ? 1 : 0.5
          tooltip += `<div style="display:flex;justify-content:space-between;gap:1rem;opacity:${opacity}">
            <span><span style="color:${p.color}">●</span> ${p.seriesName}</span>
            <span style="font-weight:500">${p.value[1].toLocaleString()}</span>
          </div>`
          total += p.value[1]
        }
      })
      tooltip += `<div style="border-top:1px solid #374151;margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;font-weight:600">
        <span>Total</span>
        <span>${total.toLocaleString()}</span>
      </div>`
    } else {
      // Legacy format: single count
      tooltip += `Count: ${params[0].value[1].toLocaleString()}`
    }

    if (props.showBrushInstructions) {
      tooltip += `<div style="color: #7357D2; margin-top:8px; font-size:11px">Click bar to filter &bull; Drag to select range</div>`
    }
    return tooltip
  }

  // Build series based on format
  let series
  if (isStackedFormat) {
    series = buildStackedSeries()
  } else {
    // Legacy single series format
    const chartData = props.data.map(bucket => [bucket.timestamp, bucket.count])
    series = [{
      type: 'bar',
      data: chartData,
      itemStyle: {
        color: {
          type: "linear",
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: "rgba(255,255,255,0.6)" },
            { offset: 1, color: "rgba(255,255,255,0.1)" }
          ],
        },
        borderRadius: [4, 4, 0, 0]
      },
      emphasis: {
        itemStyle: {
          color: 'rgba(255,255,255,0.8)'
        }
      },
      barWidth: '90%'
    }]
  }

  // Calculate nice max value for y-axis
  const maxVal = maxTotalCount.value

  // Update chart with interactive features
  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      backgroundColor: '#1E252F',
      borderColor: '#2a3441',
      textStyle: { color: '#fff', fontSize: 12 },
      formatter: tooltipFormatter,
      extraCssText: 'z-index: 1000;',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(255,255,255,0.05)'
        }
      }
    },
    brush: {
      xAxisIndex: 0,
      brushStyle: {
        borderWidth: 2,
        color: 'rgba(115, 87, 210, 0.2)',
        borderColor: '#7357D2'
      },
      outOfBrush: {
        color: 'rgba(255,255,255,0.2)',
      },
      throttleType: 'debounce',
      throttleDelay: 300,
    },
    toolbox: {
      show: false,
    },
    grid: {
      left: '45px',
      right: '15px',
      top: '20px',
      bottom: '35px'
    },
    xAxis: {
      type: 'time',
      axisLine: { lineStyle: { color: '#2a3441' } },
      axisLabel: { color: '#6D7785', fontSize: 11 },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      max: maxVal,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#6D7785',
        fontSize: 11,
        formatter: (value) => {
          // Only show label for max value
          if (value === maxVal && maxVal > 0) {
            if (maxVal >= 1000000) {
              return (maxVal / 1000000).toFixed(1) + 'M'
            } else if (maxVal >= 1000) {
              return (maxVal / 1000).toFixed(1) + 'K'
            }
            return maxVal.toLocaleString()
          }
          return ''
        }
      },
      splitLine: { show: false }
    },
    series: series
  }

  chartInstance.setOption(option, true)

  // Add click handler for bars
  chartInstance.off('click') // Remove old handlers
  chartInstance.on('click', (params) => {
    if (params.componentType === 'series') {
      const clickedTime = params.value[0]

      // Find the bucket in histogram data
      const bucket = props.data.find(b => b.timestamp === clickedTime)
      if (!bucket) return

      // Calculate time range for this bucket using helper
      const interval = calculateInterval()

      // Emit bar-click event
      emit('bar-click', {
        timestamp: clickedTime,
        interval: interval
      })
    }
  })

  // Enable brush mode by default
  chartInstance.dispatchAction({
    type: 'takeGlobalCursor',
    key: 'brush',
    brushOption: {
      brushType: 'lineX',
      brushMode: 'single'
    }
  })

  // Add brush selection handler
  chartInstance.off('brushEnd') // Remove old handlers
  chartInstance.on('brushEnd', (params) => {
    const brushComponent = params.areas[0]

    if (brushComponent && brushComponent.coordRange) {
      let [startTime, endTime] = brushComponent.coordRange

      // Validate and handle backward brush
      if (startTime > endTime) {
        [startTime, endTime] = [endTime, startTime]
      }

      if (startTime && endTime) {
        // Round start time BACKWARD to the start of the bar
        const roundedStart = findBarBoundaries(startTime, 'backward')

        // Round end time FORWARD to include the complete final bar
        const roundedEnd = findBarBoundaries(endTime, 'forward')

        if (roundedStart !== null && roundedEnd !== null) {
          // Clear the brush selection visually
          chartInstance.dispatchAction({
            type: 'brush',
            command: 'clear',
            areas: []
          })

          // Emit brush-select event with rounded boundaries
          emit('brush-select', {
            startTime: roundedStart,
            endTime: roundedEnd
          })
        }
      }
    }
  })
}

// Lifecycle
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (chartInstance) {
    chartInstance.dispose()
  }
})

function handleResize() {
  if (chartInstance) {
    try {
      chartInstance.resize()
    } catch (e) {
      console.error('Error resizing chart:', e)
    }
  }
}

// Expose resize method for parent components
defineExpose({
  resize: handleResize
})
</script>

<template>
  <div ref="chartContainer" class="chart-container" :style="{ height: height }"></div>
</template>

<style scoped>
.chart-container {
  width: 100%;
  cursor: pointer;
}
</style>
